import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Search, Mail, Phone, Trash2, ArrowRight, ClipboardPaste, Sparkles, Loader2, Star, SortAsc, Calendar, DollarSign, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MobileNav from "../components/MobileNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, name_az, name_za, balance, priority
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "", date_of_birth: "", ssn_full: "", is_priority: false, balance: ""
  });
  
  // Smart Paste state
  const [showPaste, setShowPaste] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [parsing, setParsing] = useState(false);
  
  // Priority popup state
  const [showPriorityPopup, setShowPriorityPopup] = useState(false);
  const [newClientId, setNewClientId] = useState(null);
  const [newClientName, setNewClientName] = useState("");

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data || []);
    } catch (err) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };
  
  // Smart Paste handler
  const handleSmartPaste = async () => {
    if (!pastedText.trim()) {
      toast.error('Please paste client information');
      return;
    }
    
    setParsing(true);
    try {
      const res = await axios.post(`${API}/smart-parse`, { text: pastedText });
      
      if (res.data.success && res.data.data) {
        const p = res.data.data;
        
        // Convert date from MM/DD/YYYY to YYYY-MM-DD for HTML date input
        let formattedDob = '';
        if (p.date_of_birth) {
          const dobParts = p.date_of_birth.split('/');
          if (dobParts.length === 3) {
            formattedDob = `${dobParts[2]}-${dobParts[0].padStart(2, '0')}-${dobParts[1].padStart(2, '0')}`;
          }
        }
        
        setFormData({
          ...formData,
          name: p.name || '',
          email: p.email || '',
          phone: p.phone || '',
          address: p.address || '',
          city: p.city || '',
          state: p.state || '',
          zip_code: p.zip_code || '',
          date_of_birth: formattedDob,
          ssn_full: p.ssn_full || ''
        });
        setShowPaste(false);
        setPastedText('');
        toast.success(`Parsed: ${p.name || 'Client info'}`);
      } else {
        toast.error('Could not parse. Check the format.');
      }
    } catch (err) {
      toast.error('Failed to parse');
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        ...formData, 
        ssn_last4: formData.ssn_full?.slice(-4) || "",
        balance: formData.balance ? parseFloat(formData.balance) : null
      };
      const res = await axios.post(`${API}/clients`, submitData);
      toast.success("Client created!");
      setIsDialogOpen(false);
      
      // If priority wasn't set during creation, show popup
      if (!formData.is_priority && res.data?.id) {
        setNewClientId(res.data.id);
        setNewClientName(formData.name);
        setShowPriorityPopup(true);
      } else if (res.data?.id) {
        navigate(`/clients/${res.data.id}`);
      }
      
      setFormData({ name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "", date_of_birth: "", ssn_full: "", is_priority: false, balance: "" });
      fetchClients();
    } catch (err) {
      toast.error("Failed to create client");
    }
  };
  
  const handlePriorityPopupResponse = async (makePriority) => {
    if (makePriority && newClientId) {
      try {
        await axios.patch(`${API}/clients/${newClientId}/priority?is_priority=true`);
        toast.success("Client marked as priority!");
        fetchClients();
      } catch (err) {
        toast.error("Failed to set priority");
      }
    }
    setShowPriorityPopup(false);
    if (newClientId) {
      navigate(`/clients/${newClientId}`);
    }
    setNewClientId(null);
    setNewClientName("");
  };
  
  const togglePriority = async (clientId, currentPriority, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/clients/${clientId}/priority?is_priority=${!currentPriority}`);
      toast.success(currentPriority ? "Removed from priority" : "Marked as priority!");
      fetchClients();
    } catch (err) {
      toast.error("Failed to update priority");
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this client?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Client deleted");
      fetchClients();
    } catch (err) {
      toast.error("Failed to delete client");
    }
  };

  // Sort and filter clients
  const getSortedClients = () => {
    let filtered = clients.filter(c => 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      c.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    switch (sortBy) {
      case "newest":
        return filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      case "oldest":
        return filtered.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      case "name_az":
        return filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      case "name_za":
        return filtered.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
      case "balance":
        return filtered.sort((a, b) => (b.balance || 0) - (a.balance || 0));
      case "priority":
        return filtered.sort((a, b) => {
          if (a.is_priority && !b.is_priority) return -1;
          if (!a.is_priority && b.is_priority) return 1;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
      default:
        return filtered;
    }
  };
  
  const sortedClients = getSortedClients();
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div></div>;

  return (
    <div className="space-y-6">
      <MobileNav />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clients</h1>
          <p className="text-zinc-400">Manage your credit repair clients</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" />Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>
            
            {/* Smart Paste Toggle */}
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                onClick={() => setShowPaste(!showPaste)}
                className={showPaste ? "flex-1 bg-blue-600 hover:bg-blue-700" : "flex-1 bg-zinc-700 hover:bg-zinc-600"}
              >
                <ClipboardPaste className="w-4 h-4 mr-2" />
                Paste Client Info
              </Button>
            </div>
            
            {/* Smart Paste Area */}
            {showPaste && (
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 mb-4 space-y-2">
                <p className="text-xs text-zinc-400">
                  <strong className="text-blue-400">Format:</strong> NAME - John Doe, DATE OF BIRTH - 01/15/1985, etc.
                </p>
                <textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder={`NAME - Damien Salter
DATE OF BIRTH - 06/29/1978
SOCIAL SECURITY NUMBER - 561-53-6166
ADDRESS - 4202 S Victoria Ave
View Park, CA 90008
PHONE NUMBER - 323-209-8462
EMAIL - Dsalterbeauty@aol.com`}
                  className="w-full h-32 bg-zinc-900 border border-zinc-600 rounded p-2 text-sm font-mono text-white resize-none"
                  data-testid="client-paste-textarea"
                />
                <Button
                  type="button"
                  onClick={handleSmartPaste}
                  disabled={parsing || !pastedText.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {parsing ? 'Parsing...' : 'Parse & Fill Form'}
                </Button>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>City</Label><Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="bg-zinc-800 border-zinc-700" /></div>
                <div><Label>State</Label><Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} className="bg-zinc-800 border-zinc-700" /></div>
                <div><Label>ZIP</Label><Input value={formData.zip_code} onChange={(e) => setFormData({...formData, zip_code: e.target.value})} className="bg-zinc-800 border-zinc-700" /></div>
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label>SSN (optional)</Label>
                <Input type="password" value={formData.ssn_full} onChange={(e) => setFormData({...formData, ssn_full: e.target.value})} placeholder="XXX-XX-XXXX" className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label>Balance Owed (optional)</Label>
                <Input type="number" step="0.01" value={formData.balance} onChange={(e) => setFormData({...formData, balance: e.target.value})} placeholder="0.00" className="bg-zinc-800 border-zinc-700" />
              </div>
              
              {/* Priority Toggle */}
              <div className="flex items-center justify-between bg-zinc-800 border border-zinc-700 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Star className={`w-5 h-5 ${formData.is_priority ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-500'}`} />
                  <div>
                    <p className="font-medium text-sm">Mark as Priority Client</p>
                    <p className="text-xs text-zinc-400">Priority clients appear at the top</p>
                  </div>
                </div>
                <Switch 
                  checked={formData.is_priority} 
                  onCheckedChange={(checked) => setFormData({...formData, is_priority: checked})}
                  data-testid="priority-toggle"
                />
              </div>
              
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">Create Client</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input placeholder="Search clients..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-zinc-900 border-zinc-800" />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[200px] bg-zinc-900 border-zinc-800" data-testid="sort-select">
            <SortAsc className="w-4 h-4 mr-2 text-zinc-400" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="newest">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Newest First</span>
            </SelectItem>
            <SelectItem value="oldest">
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Oldest First</span>
            </SelectItem>
            <SelectItem value="name_az">
              <span className="flex items-center gap-2"><SortAsc className="w-4 h-4" /> Name (A-Z)</span>
            </SelectItem>
            <SelectItem value="name_za">
              <span className="flex items-center gap-2"><SortAsc className="w-4 h-4 rotate-180" /> Name (Z-A)</span>
            </SelectItem>
            <SelectItem value="balance">
              <span className="flex items-center gap-2"><DollarSign className="w-4 h-4" /> Balance (High to Low)</span>
            </SelectItem>
            <SelectItem value="priority">
              <span className="flex items-center gap-2"><Star className="w-4 h-4" /> Priority First</span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {sortedClients.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Users className="h-16 w-16 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400 mb-4">{searchTerm ? "No clients match your search" : "No clients yet"}</p>
            {!searchTerm && <Button onClick={() => setIsDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600">Add Your First Client</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sortedClients.map((client) => (
            <Card key={client.id} className={`bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer ${client.is_priority ? 'border-l-4 border-l-yellow-500' : ''}`} onClick={() => navigate(`/clients/${client.id}`)} data-testid={`client-card-${client.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${client.is_priority ? 'bg-gradient-to-br from-yellow-500 to-orange-600' : 'bg-gradient-to-br from-orange-500 to-red-600'}`}>
                    <span className="text-white font-bold text-lg">{client.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-lg">{client.name}</p>
                      {client.is_priority && (
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-zinc-400">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{client.email}</span>
                      {client.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.phone}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                      {client.created_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Added {formatDate(client.created_at)}
                        </span>
                      )}
                      {client.balance > 0 && (
                        <span className="flex items-center gap-1 text-green-400">
                          <DollarSign className="h-3 w-3" />
                          ${client.balance.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => togglePriority(client.id, client.is_priority, e)} 
                    className={`${client.is_priority ? 'text-yellow-500 hover:bg-yellow-500/10' : 'text-zinc-500 hover:bg-zinc-500/10'}`}
                    title={client.is_priority ? "Remove from priority" : "Mark as priority"}
                    data-testid={`priority-btn-${client.id}`}
                  >
                    <Star className={`h-4 w-4 ${client.is_priority ? 'fill-yellow-500' : ''}`} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/dispute-wizard/${client.id}`); }} className="text-orange-400 hover:bg-orange-500/10">
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={(e) => handleDelete(client.id, e)} className="text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Priority Popup Dialog */}
      <Dialog open={showPriorityPopup} onOpenChange={setShowPriorityPopup}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Mark as Priority?
            </DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 text-sm">
            Would you like to mark <span className="text-white font-medium">{newClientName}</span> as a priority client? Priority clients appear at the top of your list.
          </p>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => handlePriorityPopupResponse(false)} className="flex-1 border-zinc-700">
              No, Skip
            </Button>
            <Button onClick={() => handlePriorityPopupResponse(true)} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black">
              <Star className="w-4 h-4 mr-2 fill-black" />
              Yes, Priority
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
